import type { Components } from '@/api/generated/api';
import { matchStudentImportNames } from '@/api/resources/studentImport.api';
import { getErrorTk } from '@/utils/error.utils';
import { useMutation } from '@tanstack/vue-query';
import { EStudentNameMatch } from 'common/enum/student.import.enum';
import { isNotNullish } from 'common/utils/array.utils';
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

type TListedName = Components.Schemas.StudentImportRowDto;
type TStudent = Components.Schemas.StudentDto;
type TNameMatch = Components.Schemas.StudentNameMatchDto;

const ERROR_TK_OVERRIDES = { UNKNOWN: 'students.import.failed' };

/** A name of an uploaded list that several students of the school have, until the teacher picks who is meant. */
export interface IListedNameQuestion {
  name: TListedName;
  /** How many students of the school have the name. */
  namesakeCount: number;
  /** The namesakes the list does not name already, by another of its names or an earlier answer. */
  candidates: TStudent[];
}

/** An uploaded list once every question is answered: what the teacher reviews before anything is saved. */
export interface IListedStudents {
  /** The names no student of the school has, and those the teacher adds as new students. */
  newNames: TListedName[];
  /** The school's students the list names, for the class to take in; without a class, nobody. */
  schoolStudents: TStudent[];
  /** The names the class already has, or without a class the school: the list adds nobody for them. */
  knownCount: number;
  /** Whether the teacher picked who is meant for some of the names. */
  hasAnswers: boolean;
}

interface IListedStudentsComposable {
  /** One per name that several students of the school have, in the list's order. */
  questions: ComputedRef<IListedNameQuestion[]>;
  nameCount: ComputedRef<number>;
  errorTk: ComputedRef<string | null>;
  /** Whether matching finished successfully; false when it failed or was reset. */
  matchNames: (names: TListedName[], schoolClassId?: number) => Promise<boolean>;
  /** Drops matches and answers; a matching request in progress can no longer change them. */
  reset: () => void;
  /** Takes the student the teacher picked for a question's name, or null to add the name as a new student. */
  answer: (questionIndex: number, student: TStudent | null) => void;
  toListedStudents: () => IListedStudents;
}

/** Matches the names of an uploaded list with the school's students and keeps the teacher's answer to each question. */
export const useListedStudents = (): IListedStudentsComposable => {
  const matching = useMutation({
    mutationFn: ({ names, schoolClassId }: { names: TListedName[]; schoolClassId?: number }) =>
      matchStudentImportNames({ schoolClassId, students: names }),
    retry: false,
  });
  const matches = computed<TNameMatch[]>(() => matching.data.value ?? []);
  const errorTk = computed(() => (matching.error.value ? getErrorTk(matching.error.value, ERROR_TK_OVERRIDES) : null));
  const isForClass = ref(false);
  // per question: the student picked, null for a new student, unset until answered
  const answers = ref<(TStudent | null | undefined)[]>([]);
  let resetCount = 0;

  const ambiguousMatches = computed(() => matches.value.filter(({ match }) => match === EStudentNameMatch.AMBIGUOUS));
  const existingStudents = computed(() =>
    matches.value.flatMap(({ match, students }) => (match === EStudentNameMatch.EXISTING ? students.slice(0, 1) : [])),
  );

  const questions = computed<IListedNameQuestion[]>(() =>
    ambiguousMatches.value.map(({ name, students }, index) => {
      const named = [...existingStudents.value, ...answers.value.slice(0, index).filter(isNotNullish)];
      const candidates = students.filter((student) => !named.some(({ id }) => id === student.id));
      return { name, namesakeCount: students.length, candidates };
    }),
  );

  // the student a name stands for: the school's only one with the name, the one picked, or none for a new student
  const studentOf = (nameMatch: TNameMatch): TStudent | null =>
    nameMatch.match === EStudentNameMatch.AMBIGUOUS
      ? (answers.value[ambiguousMatches.value.indexOf(nameMatch)] ?? null)
      : (nameMatch.students.at(0) ?? null);

  const reset = (): void => {
    resetCount += 1;
    matching.reset();
    isForClass.value = false;
    answers.value = [];
  };

  const matchNames = async (names: TListedName[], schoolClassId?: number): Promise<boolean> => {
    reset();
    isForClass.value = schoolClassId != null;
    const resetCountAtStart = resetCount;
    const result = await matching.mutateAsync({ names, schoolClassId }).catch(() => null);
    return resetCount === resetCountAtStart && result !== null;
  };

  const answer = (questionIndex: number, student: TStudent | null): void => {
    answers.value[questionIndex] = student;
  };

  const toListedStudents = (): IListedStudents => {
    const listed = matches.value.filter(({ match }) => match !== EStudentNameMatch.IN_CLASS);
    const newNames = listed.filter((nameMatch) => !studentOf(nameMatch)).map(({ name }) => name);
    const schoolStudents = listed.map(studentOf).filter(isNotNullish);
    const hasAnswers = ambiguousMatches.value.length > 0;
    if (!isForClass.value) return { newNames, schoolStudents: [], knownCount: schoolStudents.length, hasAnswers };
    return { newNames, schoolStudents, knownCount: matches.value.length - listed.length, hasAnswers };
  };

  return {
    questions,
    nameCount: computed(() => matches.value.length),
    errorTk,
    matchNames,
    reset,
    answer,
    toListedStudents,
  };
};

/**
 * The intro above the names of an uploaded list, e.g. "Alles geklärt. 12 Namen sind schon in der Klasse. Prüf die
 * anderen, bevor du sie hinzufügst."; `knownSentence` says where the names the list adds nobody for are already.
 */
export const useListedStudentsIntro = (
  list: Ref<IListedStudents | null>,
  knownSentence: (count: number) => string,
): ComputedRef<string | null> => {
  const { t } = useI18n();

  return computed(() => {
    if (!list.value) return null;
    const { newNames, schoolStudents, knownCount, hasAnswers } = list.value;
    const reviewCount = newNames.length + schoolStudents.length;
    const reviewKey = knownCount ? 'students.import.reviewOthers' : 'students.import.review';
    return [
      hasAnswers ? t('students.import.cleared') : null,
      knownCount ? knownSentence(knownCount) : null,
      reviewCount ? t(reviewKey, { count: reviewCount }, reviewCount) : null,
    ]
      .filter(isNotNullish)
      .join(' ');
  });
};
